/**
 * useRecipeExtraction Hook
 *
 * A React hook for managing recipe extraction state via the recipe import API.
 * Handles the extraction of recipe data from text, URL, image, or PDF sources.
 */

import { useState, useCallback } from 'react';
import type {
  ExtractSourceType,
  ExtractedRecipe,
  ExtractedIngredient,
  ExtractRequest,
  ExtractResponse,
} from '@/types/recipeImport';

/**
 * Convert a File to base64 string
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Result is a data URL like "data:image/jpeg;base64,/9j/4AAQ..."
      // We need just the base64 part after the comma
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Hook state interface
 */
interface UseRecipeExtractionState {
  /** The extracted recipe data from the API */
  extractedRecipe: ExtractedRecipe | null;
  /** The extracted ingredients with matching information */
  extractedIngredients: ExtractedIngredient[];
  /** Suggested tags for the recipe */
  suggestedTags: string[];
  /** Whether an extraction is currently in progress */
  isExtracting: boolean;
  /** Error message if extraction failed */
  extractionError: string | null;
}

/**
 * Hook return interface
 */
interface UseRecipeExtractionReturn extends UseRecipeExtractionState {
  /** Extract a recipe from a source */
  extract: (sourceType: ExtractSourceType, content: string, file?: File) => Promise<void>;
  /** Reset the extraction state */
  reset: () => void;
}

/**
 * Custom hook for managing recipe extraction state
 *
 * @returns Extraction state and control functions
 *
 * @example
 * ```typescript
 * const {
 *   extractedRecipe,
 *   extractedIngredients,
 *   isExtracting,
 *   extractionError,
 *   extract,
 *   reset
 * } = useRecipeExtraction();
 *
 * // Extract from text
 * await extract('text', 'Recipe text here...');
 *
 * // Extract from URL
 * await extract('url', 'https://example.com/recipe');
 *
 * // Reset state
 * reset();
 * ```
 */
export function useRecipeExtraction(): UseRecipeExtractionReturn {
  const [extractedRecipe, setExtractedRecipe] = useState<ExtractedRecipe | null>(null);
  const [extractedIngredients, setExtractedIngredients] = useState<ExtractedIngredient[]>([]);
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionError, setExtractionError] = useState<string | null>(null);

  /**
   * Extract a recipe from a source
   */
  const extract = useCallback(async (
    sourceType: ExtractSourceType,
    content: string,
    file?: File
  ) => {
    // Reset previous extraction state
    setExtractedRecipe(null);
    setExtractedIngredients([]);
    setSuggestedTags([]);
    setExtractionError(null);
    setIsExtracting(true);

    try {
      // Determine content - for image/pdf, convert file to base64
      let finalContent = content;
      if ((sourceType === 'image' || sourceType === 'pdf') && file) {
        finalContent = await fileToBase64(file);
      }

      // Prepare request body
      const requestBody: ExtractRequest = {
        source_type: sourceType,
        content: finalContent,
      };

      // Make API request
      const response = await fetch('/food/api/recipe_import/extract', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      // Parse response
      const data = await response.json() as ExtractResponse;

      // Handle success
      if (response.ok && data.success) {
        setExtractedRecipe(data.recipe || null);
        setExtractedIngredients(data.ingredients || []);
        setSuggestedTags(data.suggested_tags || []);
      } else {
        // Handle API error
        setExtractionError(data.error || 'Failed to extract recipe');
      }
    } catch (error) {
      // Handle network or parsing errors
      setExtractionError('An unexpected error occurred while extracting the recipe');
    } finally {
      setIsExtracting(false);
    }
  }, []);

  /**
   * Reset the extraction state
   */
  const reset = useCallback(() => {
    setExtractedRecipe(null);
    setExtractedIngredients([]);
    setSuggestedTags([]);
    setExtractionError(null);
    setIsExtracting(false);
  }, []);

  return {
    extractedRecipe,
    extractedIngredients,
    suggestedTags,
    isExtracting,
    extractionError,
    extract,
    reset,
  };
}
